// The eight curated label fonts (M4 plan decision 5, plus three added in the
// W5 fix-up for the template styles: Cinzel Decorative, Poiret One and Bebas
// Neue — all SIL OFL via Google Fonts).
//
// All eight are served by the single Google Fonts <link> in
// app/views/layouts/application.html.erb (canvas-only faces: never page
// chrome, except the picker's own samples). A canvas can only draw a web font
// once it has actually been downloaded, so call `loadFonts(doc)` (and await it)
// before the first render and after a font change — it is SSR-safe and never
// rejects.
//
// Available faces (must match the layout <link>):
//   Cormorant Garamond  400/700, italic 400/700
//   Cinzel              400/700, NO italic (capitals-only face)
//   Playfair Display    400/700, italic 400/700
//   Great Vibes         400 only — no bold, no italic (a script; the browser
//                       would fake both, so the editor must not offer them)
//   Montserrat          400/700, italic 400/700
//   Cinzel Decorative   400/700, NO italic (monogram capitals)
//   Poiret One          400 only — no bold, no italic (Art Deco capitals)
//   Bebas Neue          400 only — no bold, no italic (bold condensed; the
//                       face is already heavy, a faked bold would smear)
// A TextElement whose weight/italic its font does not support is DRAWN as the
// nearest real face (see `resolveFace`) — the stored values are left alone so
// switching back to a font that supports them restores the look.

export type FontKey =
  | "cormorant"
  | "cinzel"
  | "playfair"
  | "great-vibes"
  | "montserrat"
  | "cinzel-decorative"
  | "poiret-one"
  | "bebas-neue";

export const FONT_KEYS: readonly FontKey[] = [
  "cormorant",
  "cinzel",
  "playfair",
  "great-vibes",
  "montserrat",
  "cinzel-decorative",
  "poiret-one",
  "bebas-neue",
] as const;

export interface FontMetrics {
  /** Cap height as a fraction of the font size (em). */
  capHeight: number;
  /** Font ascent / descent (hhea-style) as fractions of the em — used for line boxes. */
  ascent: number;
  descent: number;
}

export interface LabelFont {
  key: FontKey;
  label: string;
  /** The CSS family name exactly as Google Fonts serves it. */
  family: string;
  /** Generic fallback appended to the CSS font shorthand. */
  fallback: "serif" | "sans-serif" | "cursive";
  /** Numeric weights actually loaded: [regular, bold?]. */
  weights: readonly number[];
  supportsBold: boolean;
  supportsItalic: boolean;
  /** One-word description for the font picker. */
  description: string;
  /**
   * Fallback metrics used when the live font cannot be measured (SSR, Node,
   * before the font loads); in the browser the real values are measured once
   * per face after loading. Read from the Google Fonts TTFs (UPM 1000) of the
   * regular face: `capHeight` = the "H" glyph's yMax (what the renderer
   * measures with measureText("H"); equal to OS/2 sCapHeight except where
   * noted), `ascent`/`descent` = hhea ascender/descender (= OS/2 typo values
   * for all eight faces).
   */
  metrics: FontMetrics;
}

export const FONTS: readonly LabelFont[] = [
  {
    key: "cormorant",
    label: "Cormorant Garamond",
    family: "Cormorant Garamond",
    fallback: "serif",
    weights: [400, 700],
    supportsBold: true,
    supportsItalic: true,
    description: "Classic serif",
    metrics: { capHeight: 0.625, ascent: 0.924, descent: 0.287 },
  },
  {
    key: "cinzel",
    label: "Cinzel",
    family: "Cinzel",
    fallback: "serif",
    weights: [400, 700],
    supportsBold: true,
    supportsItalic: false,
    description: "Engraved capitals",
    metrics: { capHeight: 0.7, ascent: 0.976, descent: 0.372 },
  },
  {
    key: "playfair",
    label: "Playfair Display",
    family: "Playfair Display",
    fallback: "serif",
    weights: [400, 700],
    supportsBold: true,
    supportsItalic: true,
    description: "High-contrast serif",
    metrics: { capHeight: 0.708, ascent: 1.082, descent: 0.251 },
  },
  {
    key: "great-vibes",
    label: "Great Vibes",
    family: "Great Vibes",
    fallback: "cursive",
    weights: [400],
    supportsBold: false,
    supportsItalic: false,
    description: "Script",
    // "H" yMax 0.807 (its swash rises above the OS/2 sCapHeight of 0.75).
    metrics: { capHeight: 0.807, ascent: 0.851, descent: 0.401 },
  },
  {
    key: "montserrat",
    label: "Montserrat",
    family: "Montserrat",
    fallback: "sans-serif",
    weights: [400, 700],
    supportsBold: true,
    supportsItalic: true,
    description: "Modern sans",
    metrics: { capHeight: 0.7, ascent: 0.968, descent: 0.251 },
  },
  {
    key: "cinzel-decorative",
    label: "Cinzel Decorative",
    family: "Cinzel Decorative",
    fallback: "serif",
    weights: [400, 700],
    supportsBold: true,
    supportsItalic: false,
    description: "Monogram capitals",
    // "H" yMax 0.831 (0.853 in the 700 face): the decorative H carries a
    // flourish above the OS/2 sCapHeight of 0.70.
    metrics: { capHeight: 0.831, ascent: 0.976, descent: 0.372 },
  },
  {
    key: "poiret-one",
    label: "Poiret One",
    family: "Poiret One",
    fallback: "sans-serif",
    weights: [400],
    supportsBold: false,
    supportsItalic: false,
    description: "Deco capitals",
    metrics: { capHeight: 0.75, ascent: 0.962, descent: 0.208 },
  },
  {
    key: "bebas-neue",
    label: "Bebas Neue",
    family: "Bebas Neue",
    fallback: "sans-serif",
    weights: [400],
    supportsBold: false,
    supportsItalic: false,
    description: "Bold condensed",
    metrics: { capHeight: 0.7, ascent: 0.9, descent: 0.3 },
  },
] as const;

export const DEFAULT_FONT: FontKey = "cormorant";

const BY_KEY = new Map<FontKey, LabelFont>(FONTS.map((f) => [f.key, f]));

export function isFontKey(v: unknown): v is FontKey {
  return typeof v === "string" && BY_KEY.has(v as FontKey);
}

export function getFont(key: FontKey | string): LabelFont {
  return BY_KEY.get(key as FontKey) ?? BY_KEY.get(DEFAULT_FONT)!;
}

export interface FontFace {
  font: LabelFont;
  weight: number;
  italic: boolean;
}

/** The real face that will be drawn for a font + requested style. */
export function resolveFace(
  key: FontKey | string,
  weight: "regular" | "bold",
  italic: boolean,
): FontFace {
  const font = getFont(key);
  const bold = weight === "bold" && font.supportsBold;
  return {
    font,
    weight: bold ? font.weights[font.weights.length - 1] : font.weights[0],
    italic: italic && font.supportsItalic,
  };
}

/** CSS font shorthand for canvas `ctx.font` / FontFaceSet.load, at a px size. */
export function cssFont(face: FontFace, sizePx: number): string {
  const style = face.italic ? "italic " : "";
  // Rounded to 1/100 px so the string is stable (determinism + cache hits).
  const px = Math.round(sizePx * 100) / 100;
  return `${style}${face.weight} ${px}px "${face.font.family}", ${face.font.fallback}`;
}

/** CSS font-family value (for DOM previews such as the font <select> and the text editor). */
export function cssFamily(key: FontKey | string): string {
  const f = getFont(key);
  return `"${f.family}", ${f.fallback}`;
}

// ---- Loading ----------------------------------------------------------------

interface TextStyleLike {
  font: string;
  weight: "regular" | "bold";
  italic: boolean;
}

function facesFor(elements: readonly TextStyleLike[] | undefined): FontFace[] {
  const seen = new Map<string, FontFace>();
  const add = (f: FontFace) => seen.set(cssFont(f, 16), f);
  // Always the regular face of every curated font (the picker previews them),
  // plus Montserrat 400 for the small print on the bottle render.
  for (const font of FONTS) add({ font, weight: font.weights[0], italic: false });
  for (const el of elements ?? []) add(resolveFace(el.font, el.weight, el.italic));
  return [...seen.values()];
}

/**
 * Await every face used by `doc` (plus the regular face of each curated font).
 * Resolves when all have loaded or failed — never rejects, never hangs longer
 * than `timeoutMs` (a blocked Google Fonts request must not freeze the studio;
 * the canvas then draws with the fallback family and redraws when the
 * `document.fonts` "loadingdone" event fires). No-op during SSR.
 */
export async function loadFonts(
  doc?: { elements: readonly unknown[] } | null,
  timeoutMs = 4000,
): Promise<void> {
  if (typeof document === "undefined" || !("fonts" in document)) return;
  const texts = (doc?.elements ?? []).filter(
    (e): e is TextStyleLike =>
      typeof e === "object" && e !== null && (e as { type?: unknown }).type === "text",
  );
  const loads = facesFor(texts).map((f) =>
    document.fonts.load(cssFont(f, 32), "AaHg").catch(() => []),
  );
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  await Promise.race([Promise.all(loads).then(() => undefined), timeout]);
  if (timer) clearTimeout(timer);
}

/** True when the face is ready to draw (always false during SSR). */
export function isFaceLoaded(face: FontFace): boolean {
  if (typeof document === "undefined" || !("fonts" in document)) return false;
  try {
    return document.fonts.check(cssFont(face, 32));
  } catch {
    return false;
  }
}
